// Reads a credential or PII query parameter that the GTM bootstrap moved out of
// the URL.
//
// GTM relays page_location (the full URL) to GA, Google Ads and the Meta pixel on
// every page view, so a sign-in JWT, the /join claim code, an invite code or the
// OAuth return email and name must not be in the URL when GTM loads. The inline
// bootstrap in public/index.html, before it loads GTM, removes these parameters
// from the URL with history.replaceState and keeps them here, the way it already
// keeps ref and pt:
//
//   sessionStorage 'rxr_url_stash' = { <param name>: { value, path, ts } }
//
// `path` is the (decoded, lowercased) pathname the parameter was on and `ts` the
// time it was stashed.
//
// The values live in sessionStorage and, once a page consumed them, in this
// module's own memory, which no other script can reach. Never in a window
// property: the Meta pixel's automatic advanced matching scans window globals for
// email-shaped strings and sends their hash. A browser that blocks sessionStorage
// gets nothing back (the bootstrap dropped the values, so the visitor types them
// in again); that is the intended fail-closed behaviour.
//
// Names stripped (matched case-insensitively): token, access_token, id_token,
// code, key, secret, invite, email, firstName, lastName, phone, providerId, npi.
// Keep this list in step with urlStashNames in public/index.html.
//
// Two codes that live in the PATH are stashed too, because the bootstrap rewrites
// the URL to a neutral one (the way it does /r/:code): /i/<code> becomes /i/invite
// with the code under `invite`, and /feed-claim/<token> becomes /feed-claim/claim
// with the token under `token`. Both are stashed under the neutral path.
//
// A page that reads one of them from the URL reads it through readUrlParam, which
// returns the URL value if the URL still has it (a host the bootstrap did not
// touch, a test, a router URL) and the stashed one otherwise.
import { normalizeTrackingPath } from './productionTracking';

export const URL_STASH_KEY = 'rxr_url_stash';

// What the registration chain carries from page to page: the invite code and the
// OAuth return context. RegisterRouter forwards these when it navigates on.
export const REGISTRATION_PARAMS = ['invite', 'email', 'firstName', 'lastName', 'providerId'];

// The /register chooser (RegisterRouter rendering LandingSelector) is where the
// backend sends a no-account OAuth return whose role it does not know. The role
// cards link to the register pages with the clean query only, never the stashed
// email or name (Meta's automatic button-click event sends a clicked link's
// destination), so those pages also accept what was stashed on /register.
export const REGISTRATION_CHOOSER_PATH = '/register';
export const URL_STASH_TTL_MS = 10 * 60 * 1000;

// Values this document already consumed. A repeat read in the same document
// (React StrictMode replaying an effect or an initializer, an effect that reruns
// when the router's search changes) must return the same value, and the storage
// entry is already gone. It dies with the document: a reload finds nothing.
const consumed = new Map();

const normalizeStashPath = (pathname) => {
  const normalized = normalizeTrackingPath(pathname);
  return normalized.length > 1 && normalized.endsWith('/')
    ? normalized.slice(0, -1)
    : normalized;
};

const isFresh = (entry) =>
  !!entry &&
  typeof entry === 'object' &&
  typeof entry.value === 'string' &&
  typeof entry.ts === 'number' &&
  Date.now() - entry.ts <= URL_STASH_TTL_MS;

// `requiredPath` is one path or a list of them; an entry from any of them counts.
const matchesPath = (entry, requiredPath) => {
  if (!requiredPath || (Array.isArray(requiredPath) && !requiredPath.length)) return true;
  const entryPath = normalizeStashPath(entry.path);
  return (Array.isArray(requiredPath) ? requiredPath : [requiredPath]).some(
    (path) => normalizeStashPath(path) === entryPath,
  );
};

const loadEntries = () => {
  let parsed = null;
  try {
    parsed = JSON.parse(window.sessionStorage.getItem(URL_STASH_KEY) || 'null');
  } catch (e) {
    parsed = null;
  }
  const entries = {};
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    Object.keys(parsed).forEach((name) => {
      if (isFresh(parsed[name])) entries[name] = parsed[name];
    });
  }
  return entries;
};

const saveEntries = (entries) => {
  try {
    if (Object.keys(entries).length) {
      window.sessionStorage.setItem(URL_STASH_KEY, JSON.stringify(entries));
    } else {
      window.sessionStorage.removeItem(URL_STASH_KEY);
    }
  } catch (e) {
    // The memo below still answers a repeat read in this document.
  }
};

// The value for `name` that a page at `requiredPath` may use: this document's
// memo of what it already consumed, or the stash. When both have one, the NEWER
// wins (a later flow in this document stashed a fresh value); a leftover storage
// entry that is not newer than the memo is a stale duplicate and goes. A newer
// entry that was stashed for another path is not ours to take or delete.
const resolveStashed = (name, requiredPath, consume) => {
  if (typeof window === 'undefined') return null;
  const memo = consumed.get(name);
  const memoOk = isFresh(memo) && matchesPath(memo, requiredPath);
  const entries = loadEntries();
  const stored = entries[name];
  const storedOk = !!stored && matchesPath(stored, requiredPath);

  if (storedOk && (!memoOk || stored.ts > memo.ts)) {
    if (consume) {
      delete entries[name];
      saveEntries(entries);
      consumed.set(name, stored);
    }
    return stored;
  }
  if (memoOk) {
    if (consume && stored && stored.ts <= memo.ts) {
      delete entries[name];
      saveEntries(entries);
    }
    return memo;
  }
  return null;
};

const peekStashed = (name, requiredPath) => resolveStashed(name, requiredPath, false);

const takeStashed = (name, requiredPath) => resolveStashed(name, requiredPath, true);

// A stashed copy of a parameter the URL has again is stale.
const discardStashed = (name) => {
  consumed.delete(name);
  const entries = loadEntries();
  if (name in entries) {
    delete entries[name];
    saveEntries(entries);
  }
};

const fromUrl = (name, searchParams) => {
  if (searchParams && typeof searchParams.get === 'function') return searchParams.get(name);
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch (e) {
    return null;
  }
};

/**
 * The value of query parameter `name`: from the URL if it is there, else from the
 * stash the GTM bootstrap filled, else null. A stashed value is deleted from
 * storage once read; a repeat read in the same document returns it again.
 *
 * @param {string} name                   the parameter, as the page spells it
 * @param {URLSearchParams} [searchParams] the router's search params (any object
 *                                        with get()); defaults to window.location
 * @param {{ path?: string|string[] }} [options]
 *                                        path: only accept a stashed value that was
 *                                        stripped from this pathname (or from one of
 *                                        these). Use it for a route that owns its
 *                                        parameter, so a value stashed from another
 *                                        page is not taken.
 */
export const readUrlParam = (name, searchParams, options = {}) => {
  const urlValue = fromUrl(name, searchParams);
  if (urlValue !== null && urlValue !== undefined) {
    discardStashed(name);
    return urlValue;
  }
  const entry = takeStashed(name, options.path);
  return entry ? entry.value : null;
};

/**
 * Whether `name` is in the URL or the stash. Does not consume the stash, so a
 * page can decide where to go on the strength of a parameter and leave it for the
 * page it forwards to.
 */
export const hasUrlParam = (name, searchParams, options = {}) => {
  const urlValue = fromUrl(name, searchParams);
  if (urlValue !== null && urlValue !== undefined) return true;
  return !!peekStashed(name, options.path);
};

/**
 * `search` ('?a=1' or '') with the stashed values of `names` appended, for a page
 * that forwards its query to another route by NAVIGATING there (RegisterRouter's
 * redirects). A reader tied to a path cannot see a value stashed from the page
 * before, so the forwarder reads it here (consuming it) and puts it into the URL
 * it navigates to; the GTM bootstrap strips it and stashes it again, under the new
 * path, as soon as the navigation happens. A name the search already has is left
 * as it is. Never use the result for a rendered link (href): the stashed values
 * would sit in the DOM, where Meta's automatic button-click event reads them.
 */
export const mergeStashedParams = (search, names, options = {}) => {
  let merged = search && search !== '?' ? search : '';
  const params = new URLSearchParams(merged);
  names.forEach((name) => {
    const value = readUrlParam(name, params, options);
    if (value !== null && !params.has(name)) {
      merged += `${merged ? '&' : '?'}${name}=${encodeURIComponent(value)}`;
    }
  });
  return merged;
};

/**
 * Forget everything stashed: the storage entry and the values this document
 * consumed. Called on logout, so a credential or an email stashed for one session
 * cannot be read by the next account in this tab.
 */
export const clearUrlStash = () => {
  consumed.clear();
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(URL_STASH_KEY);
  } catch (e) {
    // Nothing to clear.
  }
};

export const __resetUrlStashForTests = ({ keepStorage = false } = {}) => {
  consumed.clear();
  if (typeof window === 'undefined' || keepStorage) return;
  try {
    window.sessionStorage.removeItem(URL_STASH_KEY);
  } catch (e) {
    // Nothing to clear.
  }
};
